import React from "react";
import { Button, Link } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { LoadState } from "../../../../../../../constants/enums";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import {
  clearCommAutomobileSchedule,
  fetchCommAutomobileScheduleCListAsync,
} from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoScheduleActions";
import {
  ICommAutomobileScheduleC,
} from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoSchedule.types";
import { useTableV2State } from "../../../../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { ControlledCustomSelect } from "../../../../../../../components/formsComponents";
import { COMMON_STATUS } from "../../../../../../../constants/constants";
import { StatusRenderer } from "../../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageTitleBar } from "../../../../../../../components/PageTitleBar/PageTitleBar";
import { AddOutlined } from "@mui/icons-material";
import { DataTableV2 } from "../../../../../../../components/TableV2/DataTableV2";
import { ICommAutomobileApplication } from "../../../../../../../redux/commAutoPolicy/commAutoAllApplications/commAutoAllApplications.types";

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Safe wrapper around `useOutletContext` that returns `undefined` instead of
 * throwing when the list is not rendered under a React Router `<Outlet>` (the
 * case inside the Lead Comm Auto panel). In the customer-policy context the
 * outlet tuple is `[customerId, customerPolicyId]`.
 */
const useOutletContextSafe = (): unknown[] | undefined => {
  try {
    return useOutletContext<unknown[]>();
  } catch {
    return undefined;
  }
};

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Optional lead-scope props. Additive/optional so the customer-policy route
 * rendering is unchanged when none are supplied (Requirements 16.1, 16.2, 16.3).
 */
interface ICommAutomobileScheduleCListProps {
  customerId?: number;
  customerPolicyId?: number;
  leadId?: string;
  onCreate?: () => void;
  onEdit?: (incrementalNumber: number | null) => void;
}

export const CommAutomobileScheduleCList: React.FC<
  ICommAutomobileScheduleCListProps
> = (props) => {
  const {
    customerId: propCustomerId,
    customerPolicyId: propCustomerPolicyId,
    leadId,
    onCreate,
    onEdit,
  } = props;

  const outlet = useOutletContextSafe();
  const customerId = (propCustomerId ?? outlet?.[0]) as any;
  const customerpolicyId = (propCustomerPolicyId ?? outlet?.[1]) as any;

  const navigate = useNavigate();
  const { listData: data, listLoading: loading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.schedule.scheduleC
  );

  const dispatch = useDispatchWrapper();

  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "ACTIVE"
  );

  const {
    state: { columnsConfig,
      dense },
    setColumnVisibility,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
  });

  const handleCreate = () => {
    if (onCreate) {
      onCreate();
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/vehicle-coverage-schedule-tab/${customerpolicyId}/scheduleC`
    );
  };

  const handleEdit = (incrementalNumber: number | null) => {
    if (onEdit) {
      onEdit(incrementalNumber);
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/vehicle-coverage-schedule-tab/${customerpolicyId}/scheduleC/${incrementalNumber}`
    );
  };

  const fetchList = () => {
    // Lead scope wins: fetch keyed by `lead_id`, never `customer_policy_id`.
    if (leadId) {
      dispatch(fetchCommAutomobileScheduleCListAsync(0, status, leadId));
    } else if (customerpolicyId) {
      dispatch(fetchCommAutomobileScheduleCListAsync(customerpolicyId, status));
    }
  };

  React.useEffect(() => {
    fetchList();

    return () => {
      dispatch(clearCommAutomobileSchedule());
    };
  }, [customerpolicyId, leadId, status]);

  const CommautomobileApplicationTableProps: IDataTableV2Props = {
    isPagination: false,
    isDataLoading: loading === LoadState.InProgress,
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {},
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS}
              />
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileApplication) => {
          return (
            <StandardTableActions
              onEditClick={() => handleEdit(row.incremental_number)}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileApplication) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "customer_policy_id",
        headerName: "ID",
        fieldName: "customer_policy_id",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 30,
        onRowCellRender: (value, row: ICommAutomobileScheduleC) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() =>
                handleEdit(
                  row.incremental_number != null
                    ? Number(row.incremental_number)
                    : null
                )
              }
            >
              {row.customer_policy_id}
            </Link>
          );
        },
      },
      {
        key: "created_by_id",
        headerName: "Created by ID",
        fieldName: "created_by_id",
        // exportCellWidth: 25,
        renderType: DataTableV2RowRenderType.TEXT,
      },
    ],
    rows: data,
      dense: dense,
    onDenseToggle: onDenseToggle,};

  return (
    <>
      <PageTitleBar
        heading="Garage Ontario"
        rightHeading={
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<AddOutlined />}
          >
            Create
          </Button>
        }
      />
      <DataTableV2 {...CommautomobileApplicationTableProps} />
    </>
  );
};
